import {Dispatch, SetStateAction} from 'react'
import {parseISO} from 'date-fns'
import {Space} from 'antd'

import styles from 'modules/project/components/Conditions.module.scss'
import {ConditionForm} from 'modules/project'
import {User} from 'modules/users'
import {useOpen} from 'common/hooks'
import {
  Box,
  Text,
  VerticalDivider,
  Button,
  UserAvatar,
  IconButton,
  Modal,
  CalendarDate,
  PenIcon,
  CloseSecondIcon,
  CalendarListIcon,
} from 'common/components'

interface ApprovalConditionsProps {
  noBorrower?: boolean
  request: any
  conditions: any[]
  setConditions: TSetConditions
}

interface ListProps {
  request: any
  conditions: any[]
  borrower?: User
  setConditions: TSetConditions
}

interface ConditionProps {
  request: any
  condition: any
  borrower?: User
  setConditions: TSetConditions
}

type TSetConditions = Dispatch<SetStateAction<any[]>>

export default function ApprovalConditions({
  noBorrower,
  request,
  conditions,
  setConditions,
}: ApprovalConditionsProps) {
  const [isOpen, handleOpen, handleClose] = useOpen()

  const borrower = request.project.borrower

  return (
    <>
      <Box mb={24}>
        <List
          request={request}
          conditions={conditions}
          borrower={borrower}
          setConditions={setConditions}
        />
        {conditions.length ? (
          <Box align="center" mt={24}>
            <Button variant="secondary" onClick={handleOpen}>
              add condition
            </Button>
          </Box>
        ) : (
          <Button
            block
            className={styles.button}
            variant="text"
            onClick={handleOpen}
          >
            <div className={styles.check}>
              <div className={styles.plug} />
            </div>
            <div className={styles.date}>
              <CalendarListIcon />
            </div>
            <VerticalDivider margin="small" />
            <p>Add condition</p>
          </Button>
        )}
      </Box>
      <Modal
        title="Add new condition"
        width={486}
        open={isOpen}
        onCancel={handleClose}
      >
        <ConditionForm
          noBorrower={noBorrower}
          request={request}
          setConditions={setConditions}
          onClose={handleClose}
        />
      </Modal>
    </>
  )
}

const List = ({request, conditions, borrower, setConditions}: ListProps) => {
  const sortedConditions = conditions ? conditions.slice() : []
  sortedConditions.sort(
    (a: any, b: any) => +parseISO(a.date) - +parseISO(b.date)
  )

  return (
    <ul>
      {sortedConditions.map((condition: any) => (
        <Condition
          key={condition.uuid}
          request={request}
          condition={condition}
          borrower={borrower}
          setConditions={setConditions}
        />
      ))}
    </ul>
  )
}

const Condition = ({
  request,
  condition,
  borrower,
  setConditions,
}: ConditionProps) => {
  const [isOpen, handleOpen, handleClose] = useOpen()

  const date = parseISO(condition.date)

  const handleRemove = () =>
    setConditions((state) =>
      state.filter((item) => item.uuid !== condition.uuid)
    )

  return (
    <>
      <li className={styles.item}>
        <CalendarDate date={date} />
        <VerticalDivider margin="small" />
        <div className={styles.content}>
          <div className={styles.block}>
            <Text mb={6}>{condition.description}</Text>
            {condition.borrowerVisibility && (
              <Space size={6}>
                <UserAvatar
                  src={borrower?.photo}
                  size={18}
                  alt={borrower ? borrower.firstName : 'Borrower'}
                />
                <Text as="span" size="2xs" weight="medium">
                  Borrower's
                </Text>
              </Space>
            )}
          </div>
        </div>
        <Space size={0}>
          <IconButton icon={<PenIcon />} onClick={handleOpen} />
          <IconButton icon={<CloseSecondIcon />} onClick={handleRemove} />
        </Space>
      </li>
      <Modal
        title="Edit condition"
        width={486}
        open={isOpen}
        onCancel={handleClose}
      >
        <ConditionForm
          request={request}
          condition={condition}
          setConditions={setConditions}
          onClose={handleClose}
        />
      </Modal>
    </>
  )
}
